/**
 * Organization Service
 * Handles organization-related operations
 */

import { RegistryClient } from "../registryClient";
import { DatabaseMigrator } from "../databaseMigrator";

/**
 * Manages organization setup and registration
 */
export class OrganizationService {
  private registryClient: RegistryClient;
  private databaseMigrator: DatabaseMigrator;

  constructor(registryClient: RegistryClient) {
    this.registryClient = registryClient;
    this.databaseMigrator = new DatabaseMigrator();
  }

  /**
   * Registers organization in the registry
   * @param slug - Organization identifier
   * @param supabaseUrl - Database URL
   * @param supabaseAnonKey - Anonymous key
   * @returns Success status
   */
  async registerOrganization(
    slug: string,
    supabaseUrl: string,
    supabaseAnonKey: string
  ): Promise<boolean> {
    return this.registryClient.registerOrganization(slug, supabaseUrl, supabaseAnonKey);
  }

  /**
   * Creates database schema for new organization
   * @param supabaseUrl - Database URL
   * @param serviceKey - Service key with admin permissions
   * @returns Success status
   */
  async createOrganizationSchema(
    supabaseUrl: string,
    serviceKey: string
  ): Promise<boolean> {
    return this.databaseMigrator.createOrganizationSchema(supabaseUrl, serviceKey);
  }

  /**
   * Complete organization setup (schema + registry)
   * 
   * @param slug - Organization identifier
   * @param supabaseUrl - Database URL
   * @param supabaseAnonKey - Anonymous key
   * @param supabaseServiceKey - Service key
   * @returns Setup result
   */
  async setupNewOrganization(
    slug: string,
    supabaseUrl: string,
    supabaseAnonKey: string,
    supabaseServiceKey: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // 1. Crear esquema de base de datos
      const schemaCreated = await this.createOrganizationSchema(
        supabaseUrl,
        supabaseServiceKey
      );

      if (!schemaCreated) {
        return {
          success: false,
          error: "Failed to create organization schema",
        };
      }

      // 2. Registrar en el registry
      const registered = await this.registerOrganization(
        slug,
        supabaseUrl,
        supabaseAnonKey
      );

      if (!registered) {
        return {
          success: false,
          error: "Failed to register organization in registry",
        };
      }

      return { success: true };
    } catch (error) {
      console.error("Error setting up organization:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }
}
